import z from "zod";

export const AuthenticationType = z.enum(['none', 'bearer', 'http', 'AWS', 'proxy']);
export type AuthenticationType = z.infer<typeof AuthenticationType>;

export const CirrusConfigTheme = z.union([
    z.string(), // Theme as string (e.g. "dark", "modern")
    z.object({
        accent: z.string(),
        backgroundDark: z.string(),
        backgroundLight: z.string(),
        foregroundDark: z.string(),
        foregroundLight: z.string(),
        highlightColor: z.string(),
    }),
]).optional().describe("A custom theme to be used in the reader instance");


export const PublicationType = z.enum(['EPUB', 'PDF', 'WP']);
export type PublicationType = z.infer<typeof PublicationType>;
export const AllowedFormatsFree = [PublicationType.enum.EPUB, PublicationType.enum.PDF] as const;
export const AllowedFormatsFreeSet = new Set<PublicationType>(AllowedFormatsFree);
export const AllowedFormatsBase = [PublicationType.enum.EPUB, PublicationType.enum.PDF] as const;
export const AllowedFormatsBaseSet = new Set<PublicationType>(AllowedFormatsBase);
export const AllowedFormatsPro = [PublicationType.enum.EPUB, PublicationType.enum.PDF, PublicationType.enum.WP] as const;
export const AllowedFormatsProSet = new Set<PublicationType>(AllowedFormatsPro);

export const RendererType = z.enum(['__responsive__', 'FlipBookRenderer', 'StackRenderer', 'SingleDocumentScrollRenderer', 'SinglePageSwipeRenderer', 'SpreadSwipeRenderer']);
export type RendererType = z.infer<typeof RendererType>;
export const AllowedRenderersFree = [RendererType.enum.SinglePageSwipeRenderer, RendererType.enum.SpreadSwipeRenderer] as const;
export const AllowedRenderersFreeSet = new Set<RendererType>(AllowedRenderersFree);
export const AllowedRenderersBase = [RendererType.enum.SinglePageSwipeRenderer, RendererType.enum.SpreadSwipeRenderer] as const;
export const AllowedRenderersBaseSet = new Set<RendererType>(AllowedRenderersBase);
export const AllowedRenderersPro = [RendererType.enum.__responsive__, RendererType.enum.FlipBookRenderer, RendererType.enum.StackRenderer, RendererType.enum.SingleDocumentScrollRenderer, RendererType.enum.SinglePageSwipeRenderer, RendererType.enum.SpreadSwipeRenderer] as const;
export const AllowedRenderersProSet = new Set<RendererType>(AllowedRenderersPro);

export const CustomFont = z.object({
    name: z.string(),
    defaults: z.object({
        fallback: z.string()
    }),
    fontFaces: z.array(z.object({
        family: z.string(),
        mediaType: z.string(),
        src: z.string()
    }))
});

export const ReadAloudLanguage = z.enum(['PublicationLanguage', 'BrowserDefault']);

/**
 * Define the ZOD schema for the various authentication types
 * This allows us to require certain key/values dependant on the authentication type used.
 */
export const NoneAuthSchema = z.object({
  authType: z.literal(AuthenticationType.enum.none),
});

export const BearerAuthSchema = z.object({
  authType: z.literal(AuthenticationType.enum.bearer),
  bearer: z.string(),
});

export const HttpAuthSchema = z.object({
  authType: z.literal(AuthenticationType.enum.http),
  username: z.string(),
  password: z.string(),
});

export const AwsAuthSchema = z.object({
  authType: z.literal(AuthenticationType.enum.AWS),
  role: z.string(),
});

export const ProxyAuthSchema = z.object({
  authType: z.literal(AuthenticationType.enum.proxy),
});